/**
 * The list pages' one header (Findings, Record updates): the page title,
 * segmented stage tabs, and the page's own controls at the right. The
 * sidebar button is the workspace's own (it floats at the top left on
 * phones and when the sidebar is collapsed), so this header leaves room for
 * it rather than drawing a second one. It replaces the breadcrumb strip on these pages, so
 * the page has one header, not two. On phones the tabs drop to a second row
 * and scroll sideways; the controls stay as icons.
 *
 * @module components/regulation/PageHeader
 */
import type { ReactNode } from "react";

import { cn } from "~/lib/utils";
import { COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS } from "~/workspaceTitlebar";
import { Tabs, TabsList, TabsTab } from "../ui/tabs";

export interface SegmentedTab<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly count?: number;
}

/**
 * Shared with the review composer's Inspector / Comment tabs. The light track
 * is mixed from the ink because --muted is lighter than the default light
 * page, so a bg-muted track vanished.
 */
export const SEGMENTED_TABS_LIST_CLASS = "gap-0.5 rounded-lg border-0 bg-foreground/6 p-0.5 dark:bg-muted";
export const SEGMENTED_TAB_CLASS =
  "mb-0 flex h-7 items-center gap-1.5 rounded-md border-0 px-2.5 py-0 text-[13px] leading-5 text-foreground/70 hover:bg-transparent hover:text-foreground data-[active]:bg-card data-[active]:font-medium data-[active]:text-foreground data-[active]:shadow-xs dark:text-foreground/65 dark:hover:text-foreground dark:data-[active]:bg-input/60 dark:data-[active]:text-foreground";

export function SegmentedTabs<T extends string>({
  label,
  value,
  onValueChange,
  tabs,
  className,
}: {
  readonly label: string;
  readonly value: T;
  readonly onValueChange: (value: T) => void;
  readonly tabs: ReadonlyArray<SegmentedTab<T>>;
  readonly className?: string;
}) {
  return (
    <Tabs value={value} onValueChange={(next) => onValueChange(next as T)} className={cn("min-w-0", className)}>
      <TabsList aria-label={label} className={cn("w-max", SEGMENTED_TABS_LIST_CLASS)} data-segmented-tabs="">
        {tabs.map((tab) => (
          <TabsTab
            key={tab.value}
            value={tab.value}
            className={SEGMENTED_TAB_CLASS}
          >
            {tab.label}
            {tab.count !== undefined ? (
              <span className="text-[11px] font-normal tabular-nums text-muted-foreground">{tab.count}</span>
            ) : null}
          </TabsTab>
        ))}
      </TabsList>
    </Tabs>
  );
}

export function PageHeader({
  title,
  meta,
  tabs,
  actions,
}: {
  /** Absent when the page carries its own title in its body. */
  readonly title?: string;
  /** A count or short note after the title. */
  readonly meta?: ReactNode;
  readonly tabs?: ReactNode;
  readonly actions?: ReactNode;
}) {
  return (
    <header
      className={cn(
        "flex min-h-[52px] shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b border-border/60 px-4 py-2 sm:px-6 sm:py-2.5",
        COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS,
      )}
      data-page-header=""
    >
      {title !== undefined ? (
        <div className="flex min-w-0 items-center gap-2">
          <h1 className="truncate text-[15px] font-semibold">{title}</h1>
          {meta ? <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{meta}</span> : null}
        </div>
      ) : null}
      {tabs ? (
        <div
          className={cn(
            "overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
            title !== undefined && "order-last -mx-4 w-[calc(100%+2rem)] px-4 sm:order-none sm:mx-0 sm:w-auto sm:px-0",
          )}
        >
          {tabs}
        </div>
      ) : null}
      {actions ? <div className="ms-auto flex shrink-0 items-center gap-1">{actions}</div> : null}
    </header>
  );
}
